import React, { useState, useEffect } from 'react'
import { FetchRequest } from '@rails/request.js'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import { Card, CardContent } from '../ui/Card'
import { nullifyEmptyFormValues, setNestedFormValue } from '../../utils/form'
import GooglePlacesAutocomplete from '../shared/GooglePlacesAutocomplete'
import SSNInput from '../shared/SSNInput'
import Alert from '../shared/Alert'

const PROFILE_EDIT_PATH = '/users'

const ProfileEdit = ({ initialData, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    current_password: '',
    password: '',
    password_confirmation: '',
    metadata: {
      address_line1: '',
      city: '',
      state: '',
      zip_code: '',
      birthdate: '',
      ssn_last_four: '',
      ssn: '',
    },
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [redirectUrl, setRedirectUrl] = useState(null)
  const isOAuthUser = initialData?.is_oauth_user

  useEffect(() => {
    if (initialData) {
      setFormData(prevData => ({
        ...prevData,
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        metadata: {
          ...prevData.metadata,
          ...initialData.metadata,
        },
      }))
    }
  }, [initialData])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const redirectUrlParam = urlParams.get('redirect_url')
    const fromCreditReport = sessionStorage.getItem('fromCreditReport') === 'true'

    if (redirectUrlParam) {
      setRedirectUrl(redirectUrlParam)
    } else if (fromCreditReport) {
      setRedirectUrl('/my_dashboard?activeTab=credit_report')
      sessionStorage.removeItem('fromCreditReport')
    }
  }, [])

  const allFieldsFilled = () => {
    return (
      formData.first_name &&
      formData.last_name &&
      formData.metadata.address_line1 &&
      formData.metadata.city &&
      formData.metadata.state &&
      formData.metadata.zip_code &&
      formData.metadata.birthdate &&
      (formData.metadata.ssn || formData.metadata.ssn_last_four)
    )
  }

  const handleChange = e => {
    const { name, value } = e.target

    // Zip code must be 5 digits
    if (name === 'metadata.zip_code') {
      const digitsOnly = value.replace(/\D/g, '')
      let formattedZip = digitsOnly.slice(0, 5)

      // If we have more than 5 digits, add the hyphen and next 4 digits
      if (digitsOnly.length > 5) {
        formattedZip += '-' + digitsOnly.slice(5, 9)
      }

      setFormData(prev => setNestedFormValue(prev, name, formattedZip))

      return
    }

    setFormData(prev => setNestedFormValue(prev, name, value))

    // Clear any existing error for this field
    if (name.split('.').reduce((acc, key) => acc?.[key], errors)) {
      setErrors(prev => setNestedFormValue(prev, name, null))
    }

    // Clear success message if exists
    if (successMessage) {
      setSuccessMessage('')
    }
  }

  const handlePlaceSelected = address => {
    setFormData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, ...address },
    }))

    // Clear any existing errors for address fields
    if (errors.metadata) {
      const ADDRESS_FIELDS = ['address_line1', 'city', 'state', 'zip_code']

      setErrors(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          ...Object.fromEntries(ADDRESS_FIELDS.map(field => [field, null])),
        },
      }))
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrors({})
    setSuccessMessage('')

    const userData = nullifyEmptyFormValues({
      first_name: formData.first_name,
      last_name: formData.last_name,
      metadata: formData.metadata,
    })

    // Only include password fields for non-OAuth users
    if (!isOAuthUser) {
      userData.current_password = formData.current_password

      // Only include new password fields if a new password is being set
      if (formData.password) {
        userData.password = formData.password
        userData.password_confirmation = formData.password_confirmation
      }
    }

    // Validate and process SSN
    if (formData.metadata.ssn) {
      const ssn = formData.metadata.ssn.replace(/\D/g, '')
      if (ssn.length !== 9) {
        setErrors(prev => ({
          ...prev,
          metadata: { ...prev.metadata, ssn: ['Full SSN must be 9 digits'] },
        }))
        setIsSubmitting(false)
        return
      }

      // Extract last 4 digits from full SSN (like the backend does)
      const extractedLastFour = ssn.substring(5, 9)
      userData.metadata.ssn_last_four = extractedLastFour
    }
    if (formData.metadata.ssn_last_four) {
      const ssnLastFour = formData.metadata.ssn_last_four.replace(/\D/g, '')
      if (ssnLastFour.length !== 4) {
        setErrors(prev => ({
          ...prev,
          metadata: { ...prev.metadata, ssn_last_four: ['Last 4 of SSN must be 4 digits'] },
        }))
        setIsSubmitting(false)
        return
      }
    }

    if (formData.metadata.zip_code) {
      const zipCode = formData.metadata.zip_code.replace(/\D/g, '')
      if (!zipCode.match(/^\d{5}(\d{4})?$/)) {
        setErrors(prev => ({
          ...prev,
          metadata: {
            ...prev.metadata,
            zip_code: ['Please enter a valid ZIP code (e.g. 12345 or 12345-6789)'],
          },
        }))
        setIsSubmitting(false)
        return
      }
    }

    try {
      const request = new FetchRequest('PUT', PROFILE_EDIT_PATH, {
        body: { user: userData },
        responseKind: 'json',
      })

      const response = await request.perform()
      const data = await response.json

      if (response.ok) {
        setFormData(prev => ({
          ...prev,
          current_password: '',
          password: '',
          password_confirmation: '',
        }))
        setSuccessMessage('Profile updated successfully')

        if (onProfileUpdate) {
          onProfileUpdate(data.user)
        }

        // If we came from credit report flow and all required fields are filled,
        // redirect back to credit report tab
        if (redirectUrl && redirectUrl.includes('credit_report') && allFieldsFilled()) {
          window.location.href =
            '/my_dashboard?activeTab=credit_report&success=true&message=Profile updated successfully! You can now access your credit report.'
        } else if (redirectUrl) {
          window.location.href = redirectUrl
        }
      } else {
        setErrors(data.errors || {})
        if (data.error === "Current password can't be blank") {
          setErrors(prev => ({
            ...prev,
            current_password: ['Current password is required to make changes'],
          }))
        }
      }
    } catch {
      setErrors({ base: ['An error occurred while updating your profile.'] })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className='mb-4'>
      <CardContent>
        <div className='mb-4'>
          <h2 className='mb-0'>Basic Information</h2>
          <small className='text-muted d-block text-start'>
            We use AES 256-bit block-level asymmetric non-deterministic encryption to protect your
            data.
          </small>
        </div>

        {successMessage && (
          <div className='position-relative'>
            <Alert type='success'>{successMessage}</Alert>
            <button
              type='button'
              className='btn-close position-absolute top-0 end-0 mt-2 me-2'
              onClick={() => setSuccessMessage('')}
              aria-label='Close'
            />
          </div>
        )}

        {errors.base && (
          <div className='position-relative'>
            <Alert type='danger'>{errors.base.join(', ')}</Alert>
            <button
              type='button'
              className='btn-close position-absolute top-0 end-0 mt-2 me-2'
              onClick={() => setErrors(prev => ({ ...prev, base: null }))}
              aria-label='Close'
            />
          </div>
        )}

        {redirectUrl && redirectUrl.includes('credit_report') && !allFieldsFilled() && (
          <div className='position-relative'>
            <Alert type='danger'>
              Please fill out the basic information below to view your credit report.
            </Alert>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className='row g-4 mb-4'>
            <div className='col-md-6'>
              <FloatingLabel controlId='floatingFirstName' label='First Name'>
                <Form.Control
                  type='text'
                  name='first_name'
                  placeholder='First Name'
                  value={formData.first_name}
                  onChange={handleChange}
                  isInvalid={!!errors.first_name}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.first_name?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>
            </div>
            <div className='col-md-6'>
              <FloatingLabel controlId='floatingLastName' label='Last Name'>
                <Form.Control
                  type='text'
                  name='last_name'
                  placeholder='Last Name'
                  value={formData.last_name}
                  onChange={handleChange}
                  isInvalid={!!errors.last_name}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.last_name?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>
            </div>
          </div>

          <FloatingLabel controlId='floatingEmail' label='Email' className='mb-4'>
            <Form.Control
              type='email'
              name='email'
              placeholder='Email'
              value={formData.email}
              disabled
              readOnly
              className='bg-light'
            />
            <Form.Text className='text-muted'>
              Contact support if you need to update your email. It cannot be changed here.
            </Form.Text>
          </FloatingLabel>

          <div className='row g-4 mb-4'>
            <div className='col-12'>
              <GooglePlacesAutocomplete
                value={formData.metadata.address_line1}
                onChange={handleChange}
                onPlaceSelected={handlePlaceSelected}
                isInvalid={!!errors.metadata?.address_line1}
                error={errors.metadata?.address_line1?.join(', ')}
              />
            </div>
            <div className='col-md-6'>
              <FloatingLabel controlId='floatingCity' label='City'>
                <Form.Control
                  type='text'
                  name='metadata.city'
                  placeholder='City'
                  value={formData.metadata.city}
                  onChange={handleChange}
                  isInvalid={!!errors.metadata?.city}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.metadata?.city?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>
            </div>
            <div className='col-md-3'>
              <FloatingLabel controlId='floatingState' label='State'>
                <Form.Control
                  type='text'
                  name='metadata.state'
                  placeholder='State'
                  value={formData.metadata.state}
                  onChange={handleChange}
                  isInvalid={!!errors.metadata?.state}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.metadata?.state?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>
            </div>
            <div className='col-md-3'>
              <FloatingLabel controlId='floatingZip' label='Zip Code'>
                <Form.Control
                  type='text'
                  name='metadata.zip_code'
                  placeholder='Zip Code'
                  value={formData.metadata.zip_code}
                  onChange={handleChange}
                  isInvalid={!!errors.metadata?.zip_code}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.metadata?.zip_code?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>
            </div>
          </div>

          <div className='row g-4 mb-4'>
            <div className='col-md-4'>
              <FloatingLabel controlId='floatingBirthdate' label='Birthdate'>
                <Form.Control
                  type='date'
                  name='metadata.birthdate'
                  placeholder='Birthdate'
                  value={formData.metadata.birthdate}
                  onChange={handleChange}
                  isInvalid={!!errors.metadata?.birthdate}
                  max={
                    new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .split('T')[0]
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.metadata?.birthdate?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>
            </div>
            <div className='col-md-8'>
              <SSNInput
                fullSSN={formData.metadata.ssn}
                lastFour={formData.metadata.ssn_last_four}
                onChange={handleChange}
                errors={errors.metadata}
              />
            </div>
          </div>

          {!isOAuthUser && (
            <>
              <h3 className='mb-4'>Password</h3>

              <FloatingLabel
                controlId='floatingCurrentPassword'
                label='Current Password'
                className='mb-4'
              >
                <Form.Control
                  type='password'
                  name='current_password'
                  placeholder='Current Password'
                  value={formData.current_password}
                  onChange={handleChange}
                  isInvalid={!!errors.current_password}
                />
                <Form.Text>Enter your password to confirm changes.</Form.Text>
                <Form.Control.Feedback type='invalid'>
                  {errors.current_password?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>

              <FloatingLabel controlId='floatingNewPassword' label='New Password' className='mb-4'>
                <Form.Control
                  type='password'
                  name='password'
                  placeholder='New Password'
                  value={formData.password}
                  onChange={handleChange}
                  isInvalid={!!errors.password}
                />
                <Form.Text>Leave blank if you don't want to change it.</Form.Text>
                <Form.Control.Feedback type='invalid'>
                  {errors.password?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>

              <FloatingLabel
                controlId='floatingPasswordConfirmation'
                label='Confirm New Password'
                className='mb-4'
              >
                <Form.Control
                  type='password'
                  name='password_confirmation'
                  placeholder='Confirm New Password'
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  isInvalid={!!errors.password_confirmation}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.password_confirmation?.join(', ')}
                </Form.Control.Feedback>
              </FloatingLabel>
            </>
          )}

          <div className='d-flex justify-content-end gap-2'>
            <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Profile'}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ProfileEdit
